import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";

import { getZcaDesglose, type ZcaDesgloseItem } from "@/lib/api";
import { formatDate } from "@/lib/utils";

type Props = {
  open: boolean;
  zca: string;
  mercado: "MDA" | "MTR";
  fecha: string;
  hora: number | null; // null = promedio diario
  onClose: () => void;
};

const FMT = new Intl.NumberFormat("es-MX", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function fmt(v: string | null | undefined): string {
  if (v == null) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return FMT.format(n);
}

function fmtPct(v: string): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  return `${n.toFixed(4)}%`;
}

/** Contribución ponderada (precio × % / Σ%) — el aporte real de cada zona al total. */
function contribucion(precio: string | null, pct: string, sumaPct: string): number | null {
  if (precio == null) return null;
  const p = Number(precio);
  const w = Number(pct);
  const total = Number(sumaPct);
  if (!Number.isFinite(p) || !Number.isFinite(w) || !Number.isFinite(total) || total === 0) {
    return null;
  }
  return (p * w) / total;
}

export function ZcaDesgloseModal({ open, zca, mercado, fecha, hora, onClose }: Props) {
  const detalle = useQuery({
    queryKey: ["zca-desglose", zca, mercado, fecha, hora],
    queryFn: () => getZcaDesglose({ zca, mercado, fecha, hora }),
    enabled: open,
  });

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <div>
            <h2 className="font-semibold">Desglose del precio ponderado</h2>
            <div className="text-xs text-slate-500 mt-0.5">
              <span className="font-medium">{zca}</span> · {mercado} · {formatDate(fecha)}
              {hora != null ? ` · hora ${hora}` : " · promedio diario"}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-900"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-2 text-xs text-slate-600 border-b bg-slate-50">
          Fórmula:{" "}
          <code className="bg-white border rounded px-1 py-0.5">
            precio_zca = Σ(precio_zonal × porcentaje) / Σ(porcentaje)
          </code>
          {" · "}cada componente (energía, pérdidas, congestión) se calcula igual.
        </div>

        <div className="flex-1 overflow-auto">
          {detalle.isLoading && (
            <div className="px-5 py-8 text-center text-slate-500">Cargando desglose…</div>
          )}
          {detalle.isError && (
            <div className="px-5 py-8 text-center text-red-600">
              Error: {(detalle.error as Error).message}
            </div>
          )}
          {detalle.data && (
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-slate-600 text-left sticky top-0">
                <tr>
                  <th className="px-3 py-2 font-medium">Zona PND</th>
                  <th className="px-3 py-2 font-medium text-right">% influencia</th>
                  <th className="px-3 py-2 font-medium text-right">Precio</th>
                  <th className="px-3 py-2 font-medium text-right">Energía</th>
                  <th className="px-3 py-2 font-medium text-right">Pérdidas</th>
                  <th className="px-3 py-2 font-medium text-right">Congestión</th>
                  <th className="px-3 py-2 font-medium text-right border-l">
                    Contribución
                    <div className="text-[10px] font-normal text-slate-400 leading-tight">
                      precio × % / Σ%
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {detalle.data.items.map((r: ZcaDesgloseItem) => {
                  const aporte = contribucion(
                    r.precio,
                    r.porcentaje,
                    detalle.data.suma_porcentaje,
                  );
                  return (
                    <tr key={r.zona_carga} className="border-t hover:bg-slate-50">
                      <td className="px-3 py-1.5">{r.zona_carga}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">
                        {fmtPct(r.porcentaje)}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{fmt(r.precio)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">{fmt(r.ene)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">{fmt(r.per)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">{fmt(r.cng)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums font-medium border-l bg-slate-50/50">
                        {aporte != null ? FMT.format(aporte) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-slate-100 border-t-2 border-slate-300 font-medium">
                <tr>
                  <td className="px-3 py-2">Σ {detalle.data.items.length} zonas</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {fmtPct(detalle.data.suma_porcentaje)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {fmt(detalle.data.precio_total)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-600">
                    {fmt(detalle.data.ene_total)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-600">
                    {fmt(detalle.data.per_total)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-600">
                    {fmt(detalle.data.cng_total)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums border-l bg-blue-50">
                    {fmt(detalle.data.precio_total)}
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>

        <div className="px-5 py-3 border-t flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="border rounded px-4 py-1.5 text-sm hover:bg-slate-50"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
